import { useRouteError, useNavigate } from 'react-router-dom';
import { AlertCircle, Home, RotateCcw } from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';

// Components
import Button from '@/shared/components/elements/Button.jsx';
import Logo from '@/shared/components/modules/Logo.jsx';

// Assets
import logo from '@/assets/images/Logo.png';
import whiteLogo from '@/assets/images/WhiteLogo.png';

const pageVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
      when: 'beforeChildren',
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const RouteErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  if (import.meta.env.DEV) {
    console.error('Route Error:', error);
  }

  const is404 = error?.status === 404;
  const errorMessage =
    error?.statusText || error?.message || 'خطای ناشناخته‌ای رخ داده است.';

  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      className="fixed inset-0 z-[9990] flex flex-col overflow-y-auto bg-gray-50/95 backdrop-blur-3xl select-none dark:bg-[#111322]/95"
    >
      {/* Header */}
      <header className="flex w-full items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex-1" />

        <div className="flex flex-1 justify-center">
          <Logo
            src={logo}
            darkSrc={whiteLogo}
            alt="لوگو پلتفرم"
            imageClassName="h-10 sm:h-12"
            onClick={() => navigate('/', { replace: true })}
          />
        </div>

        <div className="flex-1" />
      </header>

      {/* Main Content */}
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-2xl text-center">
          {/* Error Icon */}
          <motion.div
            variants={itemVariants}
            className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-orange-100/50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-400"
          >
            <AlertCircle size={48} strokeWidth={2} />
          </motion.div>

          {/* Texts */}
          <motion.div variants={itemVariants} className="mb-10">
            <h2 className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl lg:text-4xl dark:text-white">
              {is404 ? 'صفحه مورد نظر پیدا نشد!' : 'خطا در پردازش صفحه'}
            </h2>
            <p className="mx-auto max-w-lg text-lg leading-relaxed text-gray-600 dark:text-gray-400">
              {is404
                ? 'آدرسی که به دنبال آن هستید وجود ندارد یا تغییر مسیر داده است.'
                : 'متأسفانه در پردازش اطلاعات این مسیر مشکلی پیش آمده است. لطفاً مجدداً تلاش کنید.'}
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(0)}
              className="w-full min-w-[160px] shadow-[0_8px_25px_rgba(239,89,38,0.25)] transition-transform hover:-translate-y-1 sm:w-auto"
            >
              <RotateCcw size={18} className="ml-2 inline-block" />
              تلاش مجدد
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/', { replace: true })}
              className="w-full min-w-[160px] transition-transform hover:-translate-y-1 sm:w-auto dark:border-gray-600 dark:text-gray-200"
            >
              <Home size={18} className="ml-2 inline-block" />
              بازگشت به خانه
            </Button>
          </motion.div>

          {/* Developer Error Details (Only in DEV mode) */}
          {import.meta.env.DEV && (
            <motion.div
              variants={itemVariants}
              className="mt-12 overflow-hidden rounded-3xl border border-red-500/20 bg-red-50/50 p-6 text-left dark:border-red-500/10 dark:bg-red-950/20"
              dir="ltr"
            >
              <p className="mb-2 font-bold text-red-600 dark:text-red-400">
                Route Error Details:
              </p>
              <pre className="custom-scrollbar overflow-x-auto font-mono text-sm whitespace-pre-wrap text-red-800 dark:text-red-300">
                {errorMessage}
              </pre>
            </motion.div>
          )}
        </div>
      </main>
    </motion.div>
  );
};

export default RouteErrorBoundary;
